import { Box, Button, Card, CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import { AlternativasNegociacaoCard, Item, ItensContainerCard, MensagemNegociacaoCard, NegociacaoCard, NegociacaoCardHeader } from "../../styles";
import { TipoNegociacaoPedidoDescricao } from "../../../../../../../../enum/TipoNegociacaoPedido";
import { TemposNegociacao } from "../TemposNegociacao";
import { Download, Message, OpenInNew } from "@mui/icons-material";
import { AlternativasNegociacaoPedidoReembolsoParcial } from "../AlternativasNegociacaoPedidoReembolsoParcial";
import { INegociacaoPedido } from "../../../../../../../../interface/INegociacaoPedido";
import { IPedido } from "../../../../../../../../interface/IPedido";
import { useMoeda } from "../../../../../../../../../core/hooks/useMoeda";
import { useDownload } from "../../../../../../../../../core/hooks/useDownload";
import { useState } from "react";
import { useSnackbar } from "../../../../../../../../../core/contexts/SnackbarContext";
import { validarMoedaReal } from "../../../../../../../../../core/utils/validarMoedaReal";

interface NegociacaoReembolsoParcialProps {
    negociacao: INegociacaoPedido;
    pedido: IPedido;
    aceitarNegociacao: (pedidoUuid: string) => Promise<void>;
    recusarNegociacao: (pedidoUuid: string, motivo: string) => Promise<void>;
    efetuarContrapropostaNegociacao: (pedidoUuid: string, valor: number) => Promise<void>;
    loading: boolean;
}

type OpcaoNegociacao = "ACEITAR" | "RECUSAR" | "OUTRO_VALOR" | undefined;

export const NegociacaoReembolsoParcial = ({ negociacao, pedido, aceitarNegociacao, recusarNegociacao, efetuarContrapropostaNegociacao, loading }: NegociacaoReembolsoParcialProps) => {
    const { real, number } = useMoeda();
    const { downloadPorUrl } = useDownload();
    const [opcao, setOpcao] = useState<OpcaoNegociacao>();
    const [motivo, setMotivo] = useState<string>();
    const notificar = useSnackbar();

    const handleMotivo = (motivo: string) => {
        setMotivo(motivo);
    }

    const handleOpcao = (novaOpcao: string) => {
        setOpcao(novaOpcao as OpcaoNegociacao);
    }

    const [valor, setValor] = useState<string>('');
    const handleValor = (valor: string) => {
        setValor(valor);
    }

    const handleEnviar = () => {
        if(opcao == "ACEITAR") {
            aceitarNegociacao(pedido.uuid);
        }

        if(opcao == "RECUSAR") {
            if(!motivo || motivo?.length == 0) {
                return notificar("O motivo da rejeição é obrigatório", { severity: "error" })
            }
            recusarNegociacao(pedido.uuid, motivo);
        }

        const alternativa = negociacao.alternativas[0];

        if(opcao == "OUTRO_VALOR" && alternativa) {
            const valorValidacao = validarMoedaReal(valor, 0, alternativa.metadados?.valorMaximo, true);
            if(valorValidacao) {
                return notificar(valorValidacao, { severity: "error" });
            }

            efetuarContrapropostaNegociacao(pedido.uuid, number(valor))
        }
    }

    return (
        <NegociacaoCard variant="outlined">
            <NegociacaoCardHeader>
                <Typography variant="h6">
                    {TipoNegociacaoPedidoDescricao[negociacao.tipo]}
                </Typography>
            </NegociacaoCardHeader>
            {negociacao && (
                <TemposNegociacao
                    criadoAs={negociacao.criadoAs}
                    expiradoAs={negociacao.expiradoAs}
                />
            )}
            {negociacao?.metadados?.itens?.length || negociacao?.metadados?.itensEnfeite?.length && (
                <ItensContainerCard variant="outlined">
                    {negociacao?.metadados?.itens?.map(item => {
                        const produtoPedido = pedido?.produtos?.find(produto => produto.uuid == item.identificador)
                        return (
                            <Item>
                                <Typography fontWeight={600} variant="subtitle1">
                                    {item.quantidade}X - {produtoPedido?.descricao} - {real(item.valor)}
                                </Typography>
                                <Typography fontWeight={400} variant="subtitle2" color={"GrayText"}>
                                    {item.motivo}
                                </Typography>
                            </Item>
                        );
                    })}
                    {negociacao?.metadados?.itensEnfeite?.map(item => {
                        const produtoPedido = pedido?.produtos?.find(produto => produto.uuid == item.identificadorItemPai)
                        return (
                            <Item>
                                <Typography fontWeight={600} variant="subtitle1">
                                    {item.quantidade}X - {produtoPedido?.descricao} - {real(item.valor)}
                                </Typography>
                                <Typography fontWeight={400} variant="subtitle2" color={"GrayText"}>
                                    {item.motivo}
                                </Typography>
                            </Item>
                        );
                    })}
                </ItensContainerCard>
            )}
            {negociacao?.metadados?.evidencias?.length && (
                <Card variant="outlined" sx={{ width: "100%", p: 2, overflowX: "auto" }}>
                    <Typography fontWeight={"bold"}>
                        Evidências
                    </Typography>
                    <TableContainer sx={{ width: "100%", overflowX: "auto" }}>
                        <Table>
                            <TableBody>
                                {negociacao?.metadados?.evidencias?.map(anexo => (
                                    <TableRow
                                        key={anexo.uuid}
                                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                    >
                                        <TableCell align="left">{anexo.descricao}</TableCell>
                                        <TableCell align="left">
                                            <Download
                                                sx={{ cursor: 'pointer', color: 'primary.main' }}
                                                onClick={() => downloadPorUrl(anexo.url, anexo.nome)}
                                            />
                                        </TableCell>
                                        <TableCell align="left">
                                            <OpenInNew
                                                sx={{ cursor: 'pointer', color: 'primary.main' }}
                                                onClick={() => window.open(anexo.url, '_blank')}
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Card>
            )}
            {negociacao.mensagem && (
                <MensagemNegociacaoCard variant="outlined">
                    <Box sx={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
                        <Message color="secondary" />
                        <Typography variant="subtitle1">Mensagem</Typography>
                    </Box>
                    <Typography variant="subtitle2">
                        {negociacao.mensagem}
                    </Typography>
                </MensagemNegociacaoCard>
            )}
            <AlternativasNegociacaoCard>
                <Typography variant="subtitle1">Escolha uma das alternativas para responder:</Typography>
                {negociacao?.alternativas?.map((alternativa, index) => {
                    return <AlternativasNegociacaoPedidoReembolsoParcial 
                        alternativa={alternativa} 
                        negociacao={negociacao} 
                        opcao={opcao}
                        handleOpcao={handleOpcao}
                        motivo={motivo}
                        handleMotivo={handleMotivo}
                        valor={valor}
                        handleValor={handleValor}
                    />
                })}
            </AlternativasNegociacaoCard>
            <Button onClick={handleEnviar} variant={loading ? "outlined" : "contained"} fullWidth size="large" disabled={loading || !opcao}>
                {loading ? <CircularProgress color="primary" size={25} /> : "Enviar"}
            </Button>
        </NegociacaoCard>
    );
}